
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { LanguageContext } from '@/contexts/LanguageContext';
import { LanguageContextType } from '@/types/translations';

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  image?: string;
  type?: 'website' | 'article';
  siteName?: string;
}

const SEOHead = ({ 
  title, 
  description, 
  canonicalUrl, 
  image,
  type = 'website',
  siteName = 'Estancia Corta'
}: SEOHeadProps) => {
  const location = useLocation();
  
  // Use context conditionally - don't throw error if not available
  const languageContext = useContext(LanguageContext) as LanguageContextType | undefined;
  const language = languageContext?.language || 'es'; // Default to Spanish
  
  // Construir URLs correctas
  const baseUrl = 'https://estanciacorta.com';
  const currentPath = location.pathname;
  
  // URL canónica actual
  const currentUrl = canonicalUrl || `${baseUrl}${currentPath}`;
  
  // URLs alternativas para hreflang
  let esUrl = '';
  let enUrl = '';
  
  if (currentPath.startsWith('/en')) {
    // Estamos en inglés
    const pathWithoutEn = currentPath.replace('/en', '') || '/';
    esUrl = `${baseUrl}${pathWithoutEn}`;
    enUrl = `${baseUrl}${currentPath}`;
  } else {
    // Estamos en español
    esUrl = `${baseUrl}${currentPath}`;
    enUrl = `${baseUrl}/en${currentPath === '/' ? '' : currentPath}`;
  }
  
  const defaultImage = 'https://res.cloudinary.com/dqijdgd4k/image/upload/v1733779989/estancia-corta-og-image_default.jpg';
  const ogImage = image || defaultImage;
  
  // Structured data for LocalBusiness
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": "Estancia Corta",
    "description": "Alojamientos de calidad en Manzanillo, Colima. Departamentos y hospedajes bonitos y a buen precio.",
    "url": baseUrl,
    "logo": `${baseUrl}/favicon.ico`,
    "image": ogImage,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Manzanillo",
      "addressRegion": "Colima",
      "addressCountry": "MX"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "19.1143",
      "longitude": "-104.3333"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.97",
      "reviewCount": "65",
      "bestRating": "5",
      "worstRating": "1"
    },
    "priceRange": "$$",
    "telephone": "+52-333-578-1778",
    "email": "pdrocb@gmail.com"
  };
  
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="alojamientos Manzanillo, hospedaje Manzanillo, departamentos Manzanillo, vacaciones Manzanillo, renta Manzanillo, Colima, playa" />
      <meta name="author" content="Estancia Corta" />
      <meta name="robots" content="index, follow" />
      
      {/* Hreflang tags para SEO multiidioma */}
      <link rel="alternate" hrefLang="es" href={esUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={esUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Alojamientos en Manzanillo - Estancia Corta" />
      <meta property="og:locale" content={language === 'es' ? 'es_MX' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'es' ? 'en_US' : 'es_MX'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="Alojamientos en Manzanillo - Estancia Corta" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Additional SEO meta tags */}
      <meta name="geo.region" content="MX-COL" />
      <meta name="geo.placename" content="Manzanillo" />
      <meta name="geo.position" content="19.1143;-104.3333" />
      <meta name="ICBM" content="19.1143, -104.3333" />
    </Helmet>
  );
};

export default SEOHead;
